import { useState, useEffect, useMemo, useCallback } from "react";
import {
  FaChevronLeft,
  FaChevronRight,
  FaSyncAlt,
  FaEuroSign,
  FaUsers,
  FaTrophy,
} from "react-icons/fa";
import adminCalendarService from "../../services/adminCalendarService";

/* ─── Constants ───────────────────────────────────────────── */
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const MONTHS_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const SERVICES = [
  {
    id: "speedboat",
    label: "SpeedBoat Tour",
    short: "SpeedBoat",
    color: "sky",
    icon: "🚤",
  },
  {
    id: "bayliner",
    label: "Bayliner",
    short: "Bayliner",
    color: "teal",
    icon: "⚓",
  },
  {
    id: "jet-ski",
    label: "Jet Ski",
    short: "Jet Ski",
    color: "orange",
    icon: "🌊",
  },
  {
    id: "luxury-yacht",
    label: "Luxury Yacht",
    short: "Yacht",
    color: "violet",
    icon: "⛵",
  },
  {
    id: "secret-beach-escape",
    label: "Secret Beach Escape",
    short: "S.Beach",
    color: "amber",
    icon: "🏖️",
  },
];

const COLOR_MAP = {
  sky: {
    bg: "bg-sky-100",
    text: "text-sky-700",
    bar: "bg-sky-500",
    border: "border-sky-200",
    light: "bg-sky-50",
  },
  teal: {
    bg: "bg-teal-100",
    text: "text-teal-700",
    bar: "bg-teal-500",
    border: "border-teal-200",
    light: "bg-teal-50",
  },
  orange: {
    bg: "bg-orange-100",
    text: "text-orange-700",
    bar: "bg-orange-500",
    border: "border-orange-200",
    light: "bg-orange-50",
  },
  violet: {
    bg: "bg-violet-100",
    text: "text-violet-700",
    bar: "bg-violet-500",
    border: "border-violet-200",
    light: "bg-violet-50",
  },
  amber: {
    bg: "bg-amber-100",
    text: "text-amber-700",
    bar: "bg-amber-500",
    border: "border-amber-200",
    light: "bg-amber-50",
  },
};

/* ─── Main component ──────────────────────────────────────── */
export default function RevenuePanel() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const all = await adminCalendarService.getAllBookings();
      setBookings(all);
    } catch (err) {
      console.error("RevenuePanel: error loading bookings", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /* ── Number crunching ──────────────────────────────────── */
  const { monthlyData, serviceData, allYears } = useMemo(() => {
    // Collect all available years from booking dates
    const yearSet = new Set([new Date().getFullYear()]);
    bookings.forEach((b) => {
      if (b.date) {
        const y = parseInt(b.date.slice(0, 4), 10);
        if (!isNaN(y)) yearSet.add(y);
      }
    });
    const allYears = [...yearSet].sort((a, b) => a - b);

    // Filter: selected year, exclude cancelled
    const yearBks = bookings.filter((b) => {
      if (!b.date) return false;
      const y = parseInt(b.date.slice(0, 4), 10);
      return y === year && b.status !== "cancelled";
    });

    // Build 12-month skeleton
    const monthlyData = Array.from({ length: 12 }, (_, i) => ({
      month: i,
      label: MONTHS[i],
      short: MONTHS_SHORT[i],
      bookings: 0,
      revenue: 0,
      byService: Object.fromEntries(
        SERVICES.map((s) => [s.id, { bookings: 0, revenue: 0 }]),
      ),
      // source split
      online: 0,
      external: 0,
    }));

    yearBks.forEach((b) => {
      const monthIdx = parseInt(b.date.slice(5, 7), 10) - 1;
      if (monthIdx < 0 || monthIdx > 11) return;
      const m = monthlyData[monthIdx];
      m.bookings++;
      m.revenue += Number(b.totalPrice) || 0;
      if (b._isExternal || b.source === "external") m.external++;
      else m.online++;

      const sId = b.serviceId || b.serviceType || "";
      if (m.byService[sId]) {
        m.byService[sId].bookings++;
        m.byService[sId].revenue += Number(b.totalPrice) || 0;
      }
    });

    // Per-service annual totals
    const serviceData = SERVICES.map((s) => {
      let totalBookings = 0;
      let totalRevenue = 0;
      const monthly = monthlyData.map((m) => ({
        month: m.month,
        short: m.short,
        ...m.byService[s.id],
      }));
      monthly.forEach((m) => {
        totalBookings += m.bookings;
        totalRevenue += m.revenue;
      });
      return { ...s, totalBookings, totalRevenue, monthly };
    });

    return { monthlyData, serviceData, allYears };
  }, [bookings, year]);

  /* ── Summary stats ─────────────────────────────────────── */
  const totalRevenue = monthlyData.reduce((s, m) => s + m.revenue, 0);
  const totalBookings = monthlyData.reduce((s, m) => s + m.bookings, 0);
  const maxMonthRevenue = Math.max(...monthlyData.map((m) => m.revenue), 1);
  const bestMonth = monthlyData.reduce(
    (best, m) => (m.revenue > best.revenue ? m : best),
    monthlyData[0],
  );
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();

  /* ── Render ────────────────────────────────────────────── */
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-400 text-sm">
        Loading revenue data…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-slate-900">Revenue Report</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            All channels · excludes cancelled bookings
          </p>
        </div>

        {/* Year navigator */}
        <div className="flex items-center gap-1.5">
          {allYears.length > 1 && (
            <div className="flex items-center gap-1 mr-2">
              {allYears.map((y) => (
                <button
                  key={y}
                  onClick={() => setYear(y)}
                  className={`px-2.5 py-1 text-xs font-semibold rounded-lg transition ${
                    y === year
                      ? "bg-slate-900 text-white"
                      : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                  }`}
                >
                  {y}
                </button>
              ))}
            </div>
          )}
          <button
            onClick={() => setYear((y) => y - 1)}
            className="w-8 h-8 flex items-center justify-center border border-slate-200 rounded-xl text-slate-500 hover:bg-slate-50 transition"
          >
            <FaChevronLeft className="text-xs" />
          </button>
          <span className="font-bold text-slate-800 text-sm w-10 text-center">
            {year}
          </span>
          <button
            onClick={() => setYear((y) => y + 1)}
            className="w-8 h-8 flex items-center justify-center border border-slate-200 rounded-xl text-slate-500 hover:bg-slate-50 transition"
          >
            <FaChevronRight className="text-xs" />
          </button>
          <button
            onClick={load}
            title="Refresh"
            className="w-8 h-8 flex items-center justify-center border border-slate-200 rounded-xl text-slate-400 hover:bg-slate-50 transition ml-1"
          >
            <FaSyncAlt className="text-xs" />
          </button>
        </div>
      </div>

      {/* ── Overview cards ── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-teal-50 border border-teal-200 rounded-2xl p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-teal-500 rounded-xl flex items-center justify-center flex-shrink-0">
            <FaEuroSign className="text-white text-sm" />
          </div>
          <div>
            <p className="text-2xl font-bold text-teal-800">
              €{totalRevenue.toLocaleString()}
            </p>
            <p className="text-xs text-teal-600">Total Revenue {year}</p>
          </div>
        </div>

        <div className="bg-sky-50 border border-sky-200 rounded-2xl p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-sky-500 rounded-xl flex items-center justify-center flex-shrink-0">
            <FaUsers className="text-white text-sm" />
          </div>
          <div>
            <p className="text-2xl font-bold text-sky-800">{totalBookings}</p>
            <p className="text-xs text-sky-600">Total Bookings {year}</p>
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center flex-shrink-0">
            <FaTrophy className="text-white text-sm" />
          </div>
          <div>
            <p className="text-lg font-bold text-amber-800 leading-tight">
              {bestMonth.revenue > 0 ? bestMonth.label : "—"}
            </p>
            <p className="text-xs text-amber-600">
              Best Month · €{bestMonth.revenue.toLocaleString()}
            </p>
          </div>
        </div>
      </div>

      {/* ── Monthly summary table ── */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between flex-wrap gap-2">
          <p className="text-sm font-semibold text-slate-700">
            Monthly Summary — {year}
          </p>
          <p className="text-xs text-slate-400">Excludes cancelled bookings</p>
        </div>

        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="text-left px-5 py-2.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">
                  Month
                </th>
                <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">
                  Bookings
                </th>
                <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">
                  Revenue
                </th>
                {SERVICES.map((s) => (
                  <th
                    key={s.id}
                    className="text-right px-3 py-2.5 text-xs font-semibold text-slate-400 uppercase tracking-wide whitespace-nowrap"
                  >
                    {s.short}
                  </th>
                ))}
                {/* bar column */}
                <th className="px-4 py-2.5 w-28" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {monthlyData.map((m, i) => {
                const isCurrent =
                  m.month === currentMonth && year === currentYear;
                const barWidth =
                  maxMonthRevenue > 0 ? (m.revenue / maxMonthRevenue) * 100 : 0;
                return (
                  <tr
                    key={m.month}
                    className={`${
                      isCurrent
                        ? "bg-sky-50/60"
                        : i % 2 === 0
                          ? "bg-white"
                          : "bg-slate-50/30"
                    } hover:bg-slate-50 transition-colors`}
                  >
                    <td className="px-5 py-3 font-medium text-slate-800 whitespace-nowrap">
                      {isCurrent && (
                        <span className="inline-block w-1.5 h-1.5 bg-sky-500 rounded-full mr-2 mb-0.5" />
                      )}
                      {m.label}
                    </td>
                    <td className="px-4 py-3 text-right text-slate-700">
                      {m.bookings > 0 ? (
                        <span>
                          {m.bookings}
                          {(m.online > 0 || m.external > 0) && (
                            <span className="text-[10px] text-slate-400 ml-1">
                              ({m.online}💻
                              {m.external > 0 ? ` ${m.external}🌐` : ""})
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-900">
                      {m.revenue > 0 ? (
                        `€${m.revenue.toLocaleString()}`
                      ) : (
                        <span className="text-slate-300 font-normal">—</span>
                      )}
                    </td>
                    {SERVICES.map((s) => {
                      const sd = m.byService[s.id];
                      const c = COLOR_MAP[s.color];
                      return (
                        <td key={s.id} className="px-3 py-3 text-right">
                          {sd.revenue > 0 ? (
                            <span
                              className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${c.bg} ${c.text}`}
                            >
                              €{sd.revenue.toLocaleString()}
                            </span>
                          ) : (
                            <span className="text-slate-200 text-xs">—</span>
                          )}
                        </td>
                      );
                    })}
                    <td className="px-4 py-3">
                      <div className="w-full bg-slate-100 rounded-full h-1.5">
                        <div
                          className="bg-teal-500 h-1.5 rounded-full transition-all duration-300"
                          style={{ width: `${barWidth}%` }}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-slate-200 bg-slate-50">
                <td className="px-5 py-3 font-bold text-slate-900">Total</td>
                <td className="px-4 py-3 text-right font-bold text-slate-900">
                  {totalBookings}
                </td>
                <td className="px-4 py-3 text-right font-bold text-teal-700">
                  €{totalRevenue.toLocaleString()}
                </td>
                {serviceData.map((sd) => {
                  const c = COLOR_MAP[sd.color];
                  return (
                    <td key={sd.id} className="px-3 py-3 text-right">
                      {sd.totalRevenue > 0 ? (
                        <span
                          className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${c.bg} ${c.text}`}
                        >
                          €{sd.totalRevenue.toLocaleString()}
                        </span>
                      ) : (
                        <span className="text-slate-300 text-xs">—</span>
                      )}
                    </td>
                  );
                })}
                <td className="px-4 py-3" />
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden divide-y divide-slate-100">
          {monthlyData.map((m) => {
            const isCurrent = m.month === currentMonth && year === currentYear;
            const barWidth =
              maxMonthRevenue > 0 ? (m.revenue / maxMonthRevenue) * 100 : 0;
            return (
              <div
                key={m.month}
                className={`px-4 py-3 ${isCurrent ? "bg-sky-50" : ""}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-semibold text-slate-800">
                    {isCurrent && (
                      <span className="inline-block w-1.5 h-1.5 bg-sky-500 rounded-full mr-1.5 mb-0.5" />
                    )}
                    {m.label}
                  </span>
                  <span className="text-sm font-bold text-slate-900">
                    {m.revenue > 0 ? (
                      `€${m.revenue.toLocaleString()}`
                    ) : (
                      <span className="text-slate-300 font-normal">—</span>
                    )}
                  </span>
                </div>
                <p className="text-xs text-slate-400 mb-2">
                  {m.bookings} booking{m.bookings !== 1 ? "s" : ""}
                  {m.external > 0 && ` · ${m.external} external`}
                </p>
                <div className="w-full bg-slate-100 rounded-full h-1.5 mb-2">
                  <div
                    className="bg-teal-500 h-1.5 rounded-full"
                    style={{ width: `${barWidth}%` }}
                  />
                </div>
                {m.revenue > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {SERVICES.map((s) => {
                      const sd = m.byService[s.id];
                      if (!sd.revenue) return null;
                      const c = COLOR_MAP[s.color];
                      return (
                        <span
                          key={s.id}
                          className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${c.bg} ${c.text}`}
                        >
                          {s.short} €{sd.revenue.toLocaleString()}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
          {/* Mobile total */}
          <div className="px-4 py-3 bg-slate-50 flex items-center justify-between">
            <span className="text-sm font-bold text-slate-900">Year Total</span>
            <span className="text-sm font-bold text-teal-700">
              €{totalRevenue.toLocaleString()} · {totalBookings} bookings
            </span>
          </div>
        </div>
      </div>

      {/* ── Per-package breakdown ── */}
      <div>
        <h3 className="text-sm font-semibold text-slate-700 mb-3">
          Revenue by Package — {year}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {serviceData.map((s) => {
            const c = COLOR_MAP[s.color];
            const maxSvcMonth = Math.max(...s.monthly.map((m) => m.revenue), 1);
            const pct =
              totalRevenue > 0 ? (s.totalRevenue / totalRevenue) * 100 : 0;
            const avgPerBooking =
              s.totalBookings > 0
                ? (s.totalRevenue / s.totalBookings).toFixed(0)
                : 0;

            return (
              <div
                key={s.id}
                className={`bg-white border ${c.border} rounded-2xl overflow-hidden`}
              >
                {/* Card header */}
                <div
                  className={`px-4 py-3 ${c.light} border-b ${c.border} flex items-center gap-2`}
                >
                  <span className="text-xl flex-shrink-0">{s.icon}</span>
                  <div className="min-w-0 flex-1">
                    <p className={`text-sm font-bold ${c.text} truncate`}>
                      {s.label}
                    </p>
                    <p className="text-xs text-slate-400">
                      {s.totalBookings} booking
                      {s.totalBookings !== 1 ? "s" : ""}
                      {avgPerBooking > 0 && ` · €${avgPerBooking} avg`}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className={`text-xl font-bold ${c.text}`}>
                      €{s.totalRevenue.toLocaleString()}
                    </p>
                    <p className="text-xs text-slate-400">
                      {pct.toFixed(1)}% of total
                    </p>
                  </div>
                </div>

                {/* Progress bar (share of total) */}
                <div className="px-4 pt-3 pb-1">
                  <div className="w-full bg-slate-100 rounded-full h-1.5">
                    <div
                      className={`${c.bar} h-1.5 rounded-full transition-all duration-500`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>

                {/* Mini monthly bar chart */}
                <div className="px-4 pb-3">
                  <div className="flex items-end gap-px h-8 mt-2">
                    {s.monthly.map((m) => {
                      const ht =
                        maxSvcMonth > 0 ? (m.revenue / maxSvcMonth) * 100 : 0;
                      const isCur =
                        m.month === currentMonth && year === currentYear;
                      return (
                        <div
                          key={m.month}
                          className="flex-1 flex flex-col justify-end"
                          title={`${m.short}: €${m.revenue.toLocaleString()} · ${m.bookings} booking${m.bookings !== 1 ? "s" : ""}`}
                        >
                          <div
                            className={`w-full rounded-sm ${isCur ? "opacity-100 ring-1 ring-slate-400" : "opacity-80"} ${c.bar} transition-all`}
                            style={{
                              height: `${Math.max(ht, m.revenue > 0 ? 12 : 0)}%`,
                            }}
                          />
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex items-center justify-between mt-0.5">
                    <span className="text-[9px] text-slate-300">Jan</span>
                    <span className="text-[9px] text-slate-300">Dec</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Source breakdown note ── */}
      <div className="bg-slate-50 border border-slate-200 rounded-2xl px-5 py-4">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
          Channel Key
        </p>
        <div className="flex flex-wrap gap-4 text-xs text-slate-500">
          <span>💻 Online booking (website)</span>
          <span>🌐 External / platform (Viator, GetYourGuide, etc.)</span>
        </div>
      </div>
    </div>
  );
}
